import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, Clock, CheckCircle2, XCircle, Eye } from "lucide-react";

export default function ActionInsightsPanel({
  darkMode = false,
  title = "Action Insights",
  items = [],
  isFlipped = false,
  onFlip,
  showFlipButton = true,
  overlayMode = "fixed-body", // sibling-hide | fixed-body
  /** (action: 'view'|'approve'|'reject'|'cancel', row) => void */
  onRowAction,
}) {
  const statusTone = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved") return "bg-emerald-50 text-emerald-800 border border-emerald-200/80";
    if (s === "rejected") return "bg-rose-50 text-rose-800 border border-rose-200/80";
    return "bg-amber-50 text-amber-900 border border-amber-200/80";
  };
  const normalizeStatus = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved") return "approved";
    if (s === "rejected") return "rejected";
    return "pending";
  };

  const summary = useMemo(() => {
    const out = { pending: 0, approved: 0, rejected: 0, total: items.length };
    for (const row of items) {
      const s = normalizeStatus(row?.status);
      if (s === "approved") out.approved += 1;
      else if (s === "rejected") out.rejected += 1;
      else out.pending += 1;
    }
    return out;
  }, [items]);

  const actionTabs = useMemo(() => {
    const unique = [];
    const seen = new Set();
    for (const row of items) {
      const name = String(row?.action || "General").trim() || "General";
      const key = name.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(name);
      }
    }
    return unique.length ? unique : ["General"];
  }, [items]);

  const [activeAction, setActiveAction] = useState(actionTabs[0] || "General");
  const [activeStatus, setActiveStatus] = useState("all"); // all|pending|approved|rejected

  useEffect(() => {
    if (!actionTabs.includes(activeAction)) setActiveAction(actionTabs[0] || "General");
  }, [actionTabs, activeAction]);

  const filtered = useMemo(() => {
    return items.filter((row) => {
      const action = String(row?.action || "General").trim() || "General";
      if (action !== activeAction) return false;
      if (activeStatus === "all") return true;
      return normalizeStatus(row?.status) === activeStatus;
    });
  }, [items, activeAction, activeStatus]);

  const subSummary = useMemo(() => {
    const out = { pending: 0, approved: 0, rejected: 0, total: filtered.length };
    for (const row of filtered) {
      const s = normalizeStatus(row?.status);
      if (s === "approved") out.approved += 1;
      else if (s === "rejected") out.rejected += 1;
      else out.pending += 1;
    }
    return out;
  }, [filtered]);

  const resolveEmployeeCode = (row) => {
    if (row?.employeeCode) return row.employeeCode;
    const text = String(row?.employee || "");
    const match = text.match(/\(([^)]+)\)\s*$/);
    return match?.[1] || "-";
  };

  const resolveEmployeeName = (row) => {
    if (row?.employeeName != null && String(row.employeeName).trim()) return String(row.employeeName).trim();
    const text = String(row?.employee || "").trim();
    if (!text || text === "-") return "-";
    const withoutCode = text.replace(/\s*\([^)]+\)\s*$/, "").trim();
    return withoutCode || "-";
  };

  const looksLikeDateOrTime = (s) => {
    const t = String(s || "").trim();
    if (!t || t === "-") return false;
    return /\d{1,4}[/.-]\d{1,2}/.test(t) || /:\d{2}/.test(t) || t.length > 14;
  };

  const resolvePendingAt = (row) => {
    if (row?.pendingAt != null && String(row.pendingAt).trim() && !looksLikeDateOrTime(row.pendingAt)) {
      return String(row.pendingAt).trim();
    }
    if (row?.pendingWith != null && String(row.pendingWith).trim()) {
      return String(row.pendingWith).trim();
    }
    return "-";
  };

  const insightTableColCount = onRowAction ? 6 : 5;

  const viewBtnClass = darkMode
    ? "inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#3C3C4C] text-gray-300 hover:bg-white/10 hover:text-redhalo-mint focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40"
    : "inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-redhalo-mint/40 hover:text-redhalo-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40";

  const inFlowFlip =
    overlayMode === "sibling-hide" || overlayMode === "panel";

  return (
    <div
      className={`action-insights-anchor font-sans antialiased ${isFlipped ? "is-flipped" : ""} ${
        isFlipped && inFlowFlip ? "flex min-h-0 min-w-0 flex-1 flex-col" : ""
      }`}
    >
      {/* Hide all sibling body content when insights are flipped (anchor must be direct sibling of module body) */}
      {overlayMode === "sibling-hide" && (
        <style>{`
          .action-insights-anchor.is-flipped ~ * { display: none !important; }
        `}</style>
      )}

      {showFlipButton && (
        <div className="mb-3 flex shrink-0 justify-end">
          <button
            type="button"
            onClick={onFlip}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border backdrop-blur-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#121212] ${
              darkMode
                ? "border-redhalo-red/70 bg-redhalo-navy/40 text-redhalo-mint hover:bg-redhalo-navy/60"
                : "border-redhalo-red bg-redhalo-mint/40 text-redhalo-navy-dark hover:bg-redhalo-mint/60"
            }`}
            title={isFlipped ? "Back to module page" : "Flip to insights view"}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {isFlipped ? "Back" : "Flip Insights"}
          </button>
        </div>
      )}

      {isFlipped && (
        <div
          className={`${
            overlayMode === "fixed-body"
              ? "fixed inset-0 z-[110] m-0 border-0 overflow-y-auto"
              : inFlowFlip
              ? "min-h-0 flex-1 overflow-y-auto"
              : ""
          } ${overlayMode === "fixed-body" ? "rounded-none" : "rounded-lg"} border overflow-hidden ${
            darkMode
              ? "border-[#3C3C4C] bg-[#1D1D1D]"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className="px-4 py-4">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className={`text-lg font-bold tracking-tight ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
                {title}
              </h3>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">
                Flip view is reversible and scoped to this module body.
              </div>
            </div>

            {/* Action types — underlined tab strip (Employee.jsx structures / page tabs pattern) */}
            <div
              className={`mb-3 rounded-md border px-2 pt-2 pb-0 ${
                darkMode ? "border-[#3C3C4C] bg-[#1C1C1C]" : "border-[#165b5d]/30 bg-white"
              }`}
            >
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5 px-1">
                Action Tabs
              </div>
              <div
                className={`flex min-h-[2.25rem] overflow-x-auto scrollbar-hide border-b ${
                  darkMode ? "border-[#3C3C4C]" : "border-gray-300"
                }`}
              >
                {actionTabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveAction(tab)}
                    className={`shrink-0 px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-[#1C1C1C] ${
                      activeAction === tab
                        ? darkMode
                          ? "-mb-px border-b-2 border-redhalo-red text-redhalo-red"
                          : "-mb-px border-b-2 border-redhalo-red text-redhalo-navy"
                        : darkMode
                        ? "border-b-2 border-transparent text-gray-400 hover:text-gray-100"
                        : "border-b-2 border-transparent text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
              <div className={`rounded-md border p-2.5 ${darkMode ? "border-[#3C3C4C] bg-[#242424]" : "border-gray-200 bg-gray-50/90"}`}>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Total Actions</div>
                <div className={`mt-1 text-lg leading-none font-bold ${darkMode ? "text-gray-100" : "text-gray-900"}`}>{summary.total}</div>
              </div>
              <div className={`rounded-md border p-2.5 ${darkMode ? "border-[#3C3C4C] bg-[#242424]" : "border-gray-200 bg-gray-50/90"}`}>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Pending</div>
                <div className="mt-1 text-lg leading-none font-bold text-amber-700 dark:text-amber-400">{summary.pending}</div>
              </div>
              <div className={`rounded-md border p-2.5 ${darkMode ? "border-[#3C3C4C] bg-[#242424]" : "border-gray-200 bg-gray-50/90"}`}>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Approved</div>
                <div className="mt-1 text-lg leading-none font-bold text-emerald-700 dark:text-emerald-400">{summary.approved}</div>
              </div>
              <div className={`rounded-md border p-2.5 ${darkMode ? "border-[#3C3C4C] bg-[#242424]" : "border-gray-200 bg-gray-50/90"}`}>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Rejected</div>
                <div className="mt-1 text-lg leading-none font-bold text-rose-700 dark:text-rose-400">{summary.rejected}</div>
              </div>
            </div>

            {/* Status filters directly above the data table */}
            <div
              className={`mb-3 overflow-hidden rounded-md border ${
                darkMode ? "border-[#3C3C4C] bg-[#222]" : "border-gray-200 bg-white"
              }`}
            >
              <div className="px-2 pt-2 pb-0">
                <div
                  className={`flex min-h-[2.25rem] overflow-x-auto scrollbar-hide border-b ${
                    darkMode ? "border-[#3C3C4C]" : "border-gray-300"
                  }`}
                >
                  {[
                    { id: "all", label: `All (${subSummary.total})` },
                    { id: "pending", label: `Pending (${subSummary.pending})` },
                    { id: "approved", label: `Approved (${subSummary.approved})` },
                    { id: "rejected", label: `Rejected (${subSummary.rejected})` },
                  ].map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setActiveStatus(s.id)}
                      className={`shrink-0 px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-[#1C1C1C] ${
                        activeStatus === s.id
                          ? darkMode
                            ? "-mb-px border-b-2 border-redhalo-red text-redhalo-red"
                            : "-mb-px border-b-2 border-redhalo-red text-redhalo-navy"
                          : darkMode
                          ? "border-b-2 border-transparent text-gray-400 hover:text-gray-100"
                          : "border-b-2 border-transparent text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className={darkMode ? "bg-[#2D2D2D] text-gray-300" : "bg-gray-200/80 text-gray-700"}>
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide px-3 py-2 w-12">Sl No</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide px-3 py-2">Code</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide px-3 py-2">Name</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide px-3 py-2">Status</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide px-3 py-2">Pending At</th>
                    {onRowAction && (
                      <th className="w-12 px-2 py-2 text-center text-xs font-bold uppercase tracking-wide">
                        <span className="sr-only">View</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td
                        colSpan={insightTableColCount}
                        className={`px-3 py-6 text-center ${darkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        No records under this action/status.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((row, idx) => (
                        <tr
                          key={row.id || `${row.action}-${idx}`}
                          className={`${darkMode ? "border-t border-[#3C3C4C]" : "border-t border-gray-200"}`}
                        >
                          <td className={`px-3 py-2 ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                            {idx + 1}
                          </td>
                          <td className={`px-3 py-2 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
                            {resolveEmployeeCode(row)}
                          </td>
                          <td className={`px-3 py-2 ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
                            {resolveEmployeeName(row)}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold ${statusTone(row.status)}`}
                            >
                              {row.status || "Pending"}
                            </span>
                          </td>
                          <td className={`px-3 py-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                            {resolvePendingAt(row)}
                          </td>
                          {onRowAction && (
                            <td className="px-2 py-2 text-center align-middle">
                              <button
                                type="button"
                                onClick={() => onRowAction("view", row)}
                                className={viewBtnClass}
                                title="View request"
                                aria-label="View request"
                              >
                                <Eye className="h-3.5 w-3.5" aria-hidden />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
